import {
  GRAPHEME_TILE_DATA_PAGE_SIZE,
  type PagedGraphemeTileData,
  usePagedGraphemeTileData,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.glyphs";
import arrayLength from "@-/com.npmjs.extra-array__length";
import useLatest from "@-/com.npmjs.use-latest";
import { useCallback, useMemo, useState } from "react";

/** Tiles revealed per `revealMore` — one tile-data page, so a reveal never outruns its data. */
export const MORPHEME_GLYPH_TILE_GRID_PAGE_SIZE = GRAPHEME_TILE_DATA_PAGE_SIZE;

export type RevealedMorphemeGlyphTileGrid = {
  /** More of `graphemeList` is still hidden — render a "more below" footer and wire `revealMore`. */
  hasMore: boolean;
  /** Grow the revealed window by one page (clamped to the list length). Stable identity. */
  revealMore: () => void;
  /** Tile data for `MorphemeGlyphTileGridView`; paged in behind the revealed window. */
  tileData: PagedGraphemeTileData;
  /** The leading slice of `graphemeList` to render. */
  visibleGraphemeList: string[];
};

/**
 * A glyph grid revealed incrementally — the `useDeckSectionCards` pattern for a grapheme list.
 *
 * Two independent things grow together:
 * - the REVEALED window (`revealMore`, wired to the scroller's end-reached): how many graphemes are
 *   rendered at all. A due queue is hundreds of graphemes; mounting a tile for every one of them
 *   at once is what made the summary's grid arrive seconds after its greeting.
 * - the TILE DATA (stage + pinyin) for the revealed window, paged in by `usePagedGraphemeTileData`.
 *   A tile paints with placeholders until its page lands, then re-renders with the real values.
 *
 * The revealed window survives a list refresh (a refetched due list must not snap the scroll
 * position back to the top); it is only clamped to the current list length.
 *
 * `graphemeList` must already be ordered and de-duplicated.
 */
export function useRevealedMorphemeGlyphTileGrid({
  enabled = true,
  graphemeList,
}: {
  /** Gate the tile-data fetch (e.g. a blurred tab) — reveal state is kept either way. */
  enabled?: boolean;
  graphemeList: readonly string[];
}): RevealedMorphemeGlyphTileGrid {
  const [revealedCount, setRevealedCount] = useState(MORPHEME_GLYPH_TILE_GRID_PAGE_SIZE);
  const totalCount = arrayLength(graphemeList);
  const visibleCount = Math.min(revealedCount, totalCount);
  const visibleGraphemeList = useMemo(
    () => graphemeList.slice(0, visibleCount),
    [graphemeList, visibleCount],
  );

  const tileData = usePagedGraphemeTileData({
    enabled,
    graphemes: graphemeList,
    requiredCount: visibleCount,
  });

  const totalCountRef = useLatest(totalCount);
  const revealMore = useCallback(() => {
    setRevealedCount((count) =>
      count >= totalCountRef.current ? count : count + MORPHEME_GLYPH_TILE_GRID_PAGE_SIZE,
    );
  }, [totalCountRef]);

  return {
    hasMore: visibleCount < totalCount,
    revealMore,
    tileData,
    visibleGraphemeList,
  };
}
